import { Component } from "@angular/core";
import { ColDef, GridOptions } from "ag-grid-community";

import { CaseContextRecordPanelBase } from "@app/case-context/components/case-context-record-panel-base.directive";
import { CaseContextRecordGridComponent } from "@app/case-context/components/case-context-record-grid/case-context-record-grid.component";
import {
  labelValueFormatter,
  recordDateValueFormatter,
  recordCodeValueGetter,
  recordLabelValueGetter,
} from "@app/case-context/components/case-context-record-grid/case-context-record-grid-columns";
import { InmateOffenseRecord } from "@app/case-context/models/inmate-offense-record.model";
import { PHMS_CASE_CONTEXT_TERMINOLOGY } from "@app/phms/phms-case-context.terminology";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";

const DATE_CELL_CLASS = "case-context-record-grid__cell--date";
const STATUS_CELL_CLASS = "case-context-record-grid__cell--status";
const SUPPORTING_CELL_CLASS = "case-context-record-grid__cell--supporting";

/**
 * Violations section of the PHMS incarcerated-person profile (story 936). Renders the neutral
 * violation/disciplinary rows from the shared case-context record surface; accepts rows tagged
 * "violation" or "disciplinary". Empty/loading/error are handled by the shared EZ-grid wrapper.
 */
@Component({
  selector: "phms-case-context-violations-panel",
  standalone: true,
  imports: [CaseContextRecordGridComponent, TerminologyIdentityDirective],
  templateUrl: "./case-context-violations-panel.component.html",
})
export class CaseContextViolationsPanelComponent extends CaseContextRecordPanelBase {
  readonly sectionUsage = PHMS_CASE_CONTEXT_TERMINOLOGY.violations;
  readonly sectionText = createTerminologyUsageSignals(this.sectionUsage);

  readonly columnDefs: ColDef<InmateOffenseRecord>[] = [
    {
      field: "eventDate",
      headerName: "Date",
      width: 88,
      minWidth: 84,
      maxWidth: 98,
      sort: "desc",
      cellClass: DATE_CELL_CLASS,
      valueFormatter: recordDateValueFormatter,
    },
    {
      field: "recordCode",
      headerName: "Violation code",
      width: 130,
      minWidth: 120,
      cellClass: SUPPORTING_CELL_CLASS,
      valueGetter: recordCodeValueGetter,
    },
    {
      field: "recordLabel",
      headerName: "Violation",
      flex: 1,
      minWidth: 180,
      valueGetter: recordLabelValueGetter,
    },
    {
      field: "dispositionCode",
      headerName: "Disposition",
      width: 108,
      minWidth: 96,
      cellClass: SUPPORTING_CELL_CLASS,
      valueFormatter: labelValueFormatter,
    },
    {
      field: "severityCode",
      headerName: "Severity",
      width: 86,
      minWidth: 76,
      maxWidth: 104,
      cellClass: STATUS_CELL_CLASS,
      valueFormatter: labelValueFormatter,
    },
    {
      field: "recordStatusCode",
      headerName: "Status",
      width: 84,
      minWidth: 76,
      maxWidth: 102,
      cellClass: STATUS_CELL_CLASS,
      valueFormatter: labelValueFormatter,
    },
  ];

  readonly agGridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: 10,
    suppressPaginationPanel: true,
    enableCellTextSelection: true,
  };

  protected accepts(record: InmateOffenseRecord): boolean {
    return record.recordCategoryCode === "violation" || record.recordCategoryCode === "disciplinary";
  }
}
